import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Button } from '@mui/material'
import { Add } from '@mui/icons-material'
import styled from '@emotion/styled'
import storage from '../../../../utils/storageService'
import { H3 } from '../../../../layouts/TextStyles'
import { requiredArray, requiredString } from '../../../../utils/validation'
import { testApiSlice } from '../../../../features/section/testSlice'
import { QuestionState, TestQuestionState } from '../../../../features/types'
import QuestionsDnd from './components/Dnd/QuestionsDnd'
import VideoAnswersDialog from './components/Dialogs/VideoAnswersDialog'
import { ui } from '../../../../utils/uiTokens'

const styles = {
    buttonGroup: {
        mt: 2,
        display: 'flex',
        justifyContent: 'space-between',
    },
}

const QuestionSettingsPage = () => {
    const { formatMessage } = useIntl()
    const { topicTestId } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const [open, setOpen] = React.useState(false)
    const [singleQuestion, setSingleQuestion] = useState(null)
    const [createTestQuestions, { isLoading: isCreating }] =
        testApiSlice.useCreateTestQuestionsMutation()
    const { data: topicTestQuestionsData = null } =
        testApiSlice.useGetTestQuestionsQuery(Number(topicTestId))

    const topicTestQuestions: TestQuestionState = {
        topicTestId: Number(topicTestId),
        questions: [
            {
                questionId: Date.now(),
                content: '',
                questionTypeEnum: 'ONE_ANSWER',
                videoLink: '',
                position: 0,
                answers: [
                    {
                        answerId: Date.now(),
                        content: '',
                        position: 0,
                        correct: false,
                    },
                ],
            },
        ],
    }

    const validationSchema = yup.object().shape({
        questions: yup
            .array()
            .required(
                formatMessage({
                    id: 'isRequired',
                }),
            )
            .min(
                1,
                formatMessage(
                    {
                        id: 'mustAddQuestions',
                    },
                    { value: 1 },
                ),
            )
            .of(
                yup.object().shape({
                    content: requiredString({ formatMessage }),
                    answers: requiredArray({ formatMessage }).of(
                        yup.object().shape({
                            content: requiredString({
                                formatMessage,
                            }),
                        }),
                    ),
                }),
            ),
    })

    const openDialog = (question: QuestionState) => {
        setSingleQuestion(question)
        setOpen(true)
    }

    const closeDialog = () => {
        setOpen(false)
    }

    const handleDelete = (index: number) => {
        const filteredItems = formik.values.questions.filter(
            (item, qindex) => qindex !== index,
        )
        formik.setFieldValue('questions', filteredItems)
    }

    const handleAdd = () => {
        formik.setFieldValue('questions', [
            ...formik.values.questions,
            {
                questionId: Date.now(),
                content: '',
                questionTypeEnum: 'ONE_ANSWER',
                videoLink: '',
                position: 0,
                answers: [
                    {
                        answerId: Date.now(),
                        content: '',
                        position: 0,
                        correct: false,
                    },
                ],
            },
        ])
    }

    const formik = useFormik({
        initialValues: storage?.get(topicTestId) || topicTestQuestions,
        validationSchema: validationSchema,
        validateOnChange: true,
        validateOnBlur: true,

        onSubmit: async () => {
            if (isAnswersValid() && !singleQuestion) {
                try {
                    await createTestQuestions(toPayload(formik.values)).unwrap()
                    toast.success(formatMessage({ id: 'savedSuccess' }))

                    storage?.remove(topicTestId)
                    formik.resetForm()

                    if (location?.state?.test) {
                        navigate(-1)
                    }

                    navigate('/courses')
                } catch (error) {
                    // черновик в localStorage не трогаем: вопросы не сохранились
                    toast.error(formatMessage({ id: 'saveFail' }))
                }
            }
        },
    })

    const isAnswersValid = () => {
        for (const question of formik.values.questions) {
            if (question.questionTypeEnum === 'MATCHING') {
                const items = question.complianceItems || []

                if (items.length < 2) {
                    toast.error(
                        formatMessage(
                            { id: 'min_matching_required' },
                            { value: 2 },
                        ),
                    )
                    return false
                }

                if (
                    items.some(
                        (item) =>
                            !item.content ||
                            !item.correctAnswerIndexes?.length,
                    )
                ) {
                    toast.error(formatMessage({ id: 'fill_matching' }))
                    return false
                }
            } else if (!question.answers.find((a) => a.correct)) {
                toast.error(formatMessage({ id: 'choose_correct_answers' }))
                return false
            }
        }
        return true
    }

    // formik keeps the chosen answer as an index into question.answers; the API takes 1-based positions
    const toPayload = (values) => ({
        ...values,
        questions: values.questions.map((question) => ({
            ...question,
            complianceItems:
                question.questionTypeEnum === 'MATCHING'
                    ? (question.complianceItems || []).map((item) => ({
                          id: item.id,
                          content: item.content,
                          position: item.position,
                          correctAnswerPositions: (
                              item.correctAnswerIndexes || []
                          ).map((answerIndex) => answerIndex + 1),
                      }))
                    : [],
        })),
    })

    const fromResponse = (data) => ({
        ...data,
        questions: data.questions.map((question) => ({
            ...question,
            complianceItems: (question.complianceItems || []).map((item) => ({
                ...item,
                correctAnswerIndexes: (item.correctAnswerIds || [])
                    .map((answerId) =>
                        question.answers.findIndex((a) => a.id === answerId),
                    )
                    .filter((answerIndex) => answerIndex >= 0),
            })),
        })),
    })

    const changeOrders = (questions) => {
        formik.setFieldValue('questions', questions)
    }

    const saveQuestions = () => {
        if (isAnswersValid()) {
            if (formik.isValid) {
                setSingleQuestion(null)
                formik.handleSubmit()
            } else {
                toast.error(formatMessage({ id: 'fillRequiredFields' }))
            }
        }
    }

    const openVideoLinksDialog = () => {
        setSingleQuestion(null)
        setOpen(true)
    }

    useEffect(() => {
        if (topicTestQuestionsData?.questions.length > 0) {
            formik.setValues(fromResponse(topicTestQuestionsData))
        }
    }, [topicTestQuestionsData])

    useEffect(() => {
        storage?.set(topicTestId, formik.values)
    }, [formik.values])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{location?.state?.test?.title}</H3>
                    </Title>
                </Header>

                <form>
                    <QuestionsDnd
                        formik={formik}
                        setItems={(elements) => changeOrders(elements)}
                        handleDelete={handleDelete}
                        openDialog={openDialog}
                    />

                    <Box sx={styles.buttonGroup}>
                        <Button
                            endIcon={<Add />}
                            onClick={handleAdd}
                        >
                            {formatMessage({ id: 'addQuestion' })}
                        </Button>

                        <Box sx={{ display: 'flex' }}>
                            <Button
                                variant="outlined"
                                sx={{ mr: 2 }}
                                onClick={() => navigate(-1)}
                            >
                                {formatMessage({ id: 'cancel' })}
                            </Button>

                            <Button
                                variant="outlined"
                                sx={{ mr: 2 }}
                                onClick={openVideoLinksDialog}
                            >
                                {formatMessage({ id: 'videoLessonLinks' })}
                            </Button>

                            <Button
                                variant="contained"
                                onClick={() => saveQuestions()}
                            >
                                {formatMessage({ id: 'save' })}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Content>

            {open && (
                <VideoAnswersDialog
                    open={open}
                    formik={formik}
                    singleQuestion={singleQuestion}
                    isCreating={isCreating}
                    closeDialog={closeDialog}
                    confirmAction={() => formik.handleSubmit()}
                />
            )}
        </Wrapper>
    )
}

export default QuestionSettingsPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
